import { useDebouncedValue } from "@mantine/hooks";
import { useSession } from "@supabase/auth-helpers-react";
import { useEffect, useState } from "react";
import { MetabaseEmbedFrame } from "./MetabaseEmbedFrame";
const PARAMETER_DEBOUNCE_MS = 500;
export interface MetabaseGuestEmbedPreviewProps {
  dashboardId: number;
  lockedParameters: Record<string, unknown>;
}

/**
 * Admin-only preview of a guest embed for a dataset that may not exist yet. Unlike
 * `MetabaseGuestEmbed`, the dashboard and its locked parameters come from the caller
 * rather than from a stored `embed_pages` row, so this is gated on the dataset
 * CREATE/UPDATE permission server-side.
 */
export const MetabaseGuestEmbedPreview = ({
  dashboardId,
  lockedParameters
}: MetabaseGuestEmbedPreviewProps) => {
  const session = useSession();
  const [token, setToken] = useState<string>();
  const [error, setError] = useState<string>();

  // Debounce the serialised value, not the object: the parent rebuilds the parameter
  // object on every keystroke, so debouncing by identity would never settle.
  const [debouncedParameters] = useDebouncedValue(JSON.stringify(lockedParameters), PARAMETER_DEBOUNCE_MS);
  useEffect(() => {
    if (!session?.access_token || !Number.isFinite(dashboardId)) return;
    let cancelled = false;
    const fetchToken = async () => {
      try {
        const response = await fetch("/api/metabase/preview-embed-token", {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session.access_token}`,
            "Content-Type": "application/json"
          },
          body: JSON.stringify({
            dashboardId,
            lockedParameters: JSON.parse(debouncedParameters)
          })
        });
        const data = (await response.json().catch(() => null)) as {
          token?: string;
          error?: string;
        } | null;
        if (cancelled) return;
        if (!response.ok || !data?.token) {
          setError(data?.error || "Could not load the dashboard preview.");
          return;
        }
        setError(undefined);
        setToken(data.token);
      } catch (e) {
        console.error("Metabase preview token request failed:", e);
        if (!cancelled) setError("Could not load the dashboard preview.");
      }
    };
    fetchToken();
    return () => {
      cancelled = true;
    };
  }, [session?.access_token, dashboardId, debouncedParameters]);
  if (error) {
    return <div className="flex h-full w-full items-center justify-center text-gray-500">{error}</div>;
  }
  return <MetabaseEmbedFrame token={token} frameKey={`${dashboardId}:${debouncedParameters}`} data-sentry-element="MetabaseEmbedFrame" data-sentry-component="MetabaseGuestEmbedPreview" data-sentry-source-file="MetabaseGuestEmbedPreview.tsx" />;
};